import React from 'react';
import SeoHead, {
  DEFAULT_META_DESCRIPTION,
  DEFAULT_META_TITLE,
} from '../components/common/SeoHead';

export default function TermsPage() {
  return (
    <>
      <SeoHead
        title={DEFAULT_META_TITLE}
        description={DEFAULT_META_DESCRIPTION}
        path='/terms'
        disableTitleSuffix
      />
      <main className='max-w-4xl mx-auto p-6 bg-white'>
        <div className='prose prose-lg max-w-none'>
          <div className='text-center mb-8'>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>
              Terms of Service
            </h1>
            <p className='text-gray-600'>
              Date of Last Revision: July 19, 2025
            </p>
          </div>

          <div className='space-y-8'>
            {/* Acceptance Section */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Acceptance of These Terms of Service
              </h2>
              <p className='text-gray-700 mb-4'>
                Nexgo AI Labs Inc. (&quot;Nexgo,&quot; &quot;we,&quot;
                &quot;us,&quot; or &quot;our&quot;) provides our services
                through our website located at www.nexgo.ai and our related
                mobile applications, APIs, marketplace, and travel technologies
                (collectively, the &quot;Service&quot;). By accessing or using
                the Service, you agree to be bound by these Terms of Service. If
                you do not agree, do not access or use the Service.
              </p>
              <p className='text-gray-700 mb-4'>
                We may revise these Terms at any time. When we do, we will
                update the &quot;Last Revised&quot; date and notify you of
                material changes. Continued use of the Service after such
                changes constitutes your acceptance.
              </p>
              <div className='bg-yellow-50 border-l-4 border-primary p-4 mb-4'>
                <p className='text-yellow-800 font-semibold'>
                  IMPORTANT: THESE TERMS INCLUDE A BINDING ARBITRATION CLAUSE
                  AND WAIVER OF CERTAIN RIGHTS.
                </p>
              </div>
            </section>

            {/* Privacy Section */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Privacy
              </h2>
              <p className='text-gray-700'>
                Please refer to our privacy policy to understand how we collect,
                use, and protect your personal information. By using the
                Service, you consent to our practices described therein.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Description of Service */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Description of the Service
              </h2>
              <p className='text-gray-700 mb-4'>
                Nexgo is an AI-powered destination discovery and travel
                experience platform designed to help users plan, book, and
                experience customized journeys. Features include:
              </p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2'>
                <li>AI-generated travel suggestions and itineraries</li>
                <li>Flight and hotel bookings</li>
                <li>
                  Booking of experiences, activities, and local guides (Local
                  Buddies)
                </li>
                <li>Local marketplace with authentic regional products</li>
                <li>
                  Interactive map-based travel insights and recommendations
                </li>
                <li>
                  Real-time personalization based on user data and location
                </li>
              </ul>
              <p className='text-gray-700 mt-4'>
                Nexgo empowers independent service providers, local guides, and
                merchants to list their services and products for users
                globally.
              </p>
            </section>

            {/* Eligibility */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Eligibility
              </h2>
              <p className='text-gray-700'>
                The Service is intended for individuals 13 years and older. If
                you are between the ages of 13 and 18, you may use the Service
                only with the involvement and consent of a parent or legal
                guardian.
              </p>
            </section>

            {/* Registration */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Registration and Account Security
              </h2>
              <p className='text-gray-700'>
                You must provide accurate and complete registration information
                and maintain the confidentiality of your login credentials. You
                are responsible for all activities under your account. Notify us
                immediately of any unauthorized use.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Transactions Section */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Transactions, Bookings, and Payments
              </h2>

              <h3 className='text-xl font-semibold text-gray-800 mb-3'>
                Bookings
              </h3>
              <p className='text-gray-700 mb-4'>
                Bookings made on Nexgo may include:
              </p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2 mb-4'>
                <li>Direct bookings for accommodations, flights, and tours</li>
                <li>
                  Local Buddy sessions and custom experiences offered by
                  independent providers
                </li>
                <li>Marketplace purchases of regional goods</li>
              </ul>
              <p className='text-gray-700 mb-4'>
                Nexgo acts as a platform facilitating these bookings. The terms
                of the Third-Party Partner or service provider govern the final
                contract.
              </p>

              <h3 className='text-xl font-semibold text-gray-800 mb-3'>
                Cancellations and Changes
              </h3>
              <p className='text-gray-700 mb-4'>
                Until further notice, cancellations, modifications, and refunds
                must be addressed directly with the Third-Party Partner or local
                service provider. Nexgo will introduce mediation and resolution
                assistance in a future phase.
              </p>

              <h3 className='text-xl font-semibold text-gray-800 mb-3'>
                Pricing
              </h3>
              <p className='text-gray-700 mb-4'>
                Prices for travel services or goods are displayed based on
                provider input and are subject to change. Taxes and fees may
                apply and will be disclosed where applicable. We are not liable
                for typographical or pricing errors.
              </p>

              <h3 className='text-xl font-semibold text-gray-800 mb-3'>
                Payment Processing
              </h3>
              <p className='text-gray-700 mb-4'>
                Nexgo processes payments directly for some transactions using
                third-party payment gateways. By submitting your payment, you
                agree to:
              </p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2 mb-4'>
                <li>Authorized use of the chosen payment method</li>
                <li>Adherence to the terms of our payment processors</li>
                <li>Timely updates to your billing and contact information</li>
              </ul>
              <p className='text-gray-700'>
                We do not store your payment credentials on our servers.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Independent Providers */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Independent Providers and Local Buddies
              </h2>
              <p className='text-gray-700 mb-4'>
                Local Buddies and sellers on the marketplace are independent
                contractors and not employees or agents of Nexgo. Nexgo provides
                a platform and does not supervise or endorse individual
                providers. Providers are solely responsible for:
              </p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2 mb-4'>
                <li>The safety, legality, and accuracy of their services</li>
                <li>Compliance with local laws and regulations</li>
                <li>Delivering the experiences booked by users</li>
              </ul>
              <p className='text-gray-700'>
                Nexgo disclaims liability for any actions, omissions, or
                representations of independent providers.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Content Section */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Content and User Contributions
              </h2>

              <h3 className='text-xl font-semibold text-gray-800 mb-3'>
                User-Generated Content
              </h3>
              <p className='text-gray-700 mb-4'>
                In the future, users may post travel itineraries, reviews,
                photos, videos, or other materials (&quot;User Content&quot;).
                By submitting such content, you grant Nexgo a perpetual,
                worldwide, royalty-free license to use, reproduce, modify,
                display, and distribute it.
              </p>
              <p className='text-gray-700 mb-4'>
                You represent that you own or have the necessary rights to the
                content you upload and agree not to post content that:
              </p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2 mb-4'>
                <li>Violates laws or third-party rights</li>
                <li>Is false, harmful, or misleading</li>
                <li>Includes unauthorized commercial communications</li>
              </ul>
              <p className='text-gray-700 mb-4'>
                Nexgo reserves the right to remove or restrict content that
                violates these Terms.
              </p>

              <h3 className='text-xl font-semibold text-gray-800 mb-3'>
                AI-Generated Content and Data Usage
              </h3>
              <p className='text-gray-700'>You acknowledge that:</p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2'>
                <li>
                  Nexgo may use your inputs, usage patterns, location data, and
                  submitted content to improve AI recommendations, local
                  discovery features, and platform intelligence.
                </li>
                <li>
                  Nexgo owns all rights to enhancements made to its AI and GenAI
                  systems, including derivative data.
                </li>
              </ul>
            </section>

            <hr className='border-gray-300' />

            {/* Acceptable Use */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Acceptable Use
              </h2>
              <p className='text-gray-700 mb-4'>You agree not to:</p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2 mb-4'>
                <li>Violate any law or regulation</li>
                <li>
                  Infringe on the intellectual property or privacy of others
                </li>
                <li>Post harmful, obscene, or abusive content</li>
                <li>Attempt unauthorized access to any part of the Service</li>
                <li>
                  Circumvent security features or scrape, copy, or resell
                  platform data
                </li>
              </ul>
              <p className='text-gray-700'>
                Violations may result in account suspension or termination.
              </p>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Intellectual Property
              </h2>
              <p className='text-gray-700 mb-4'>
                All content, designs, AI logic, and software associated with
                Nexgo are the property of Nexgo AI Labs Inc. or its licensors.
                You may not use Nexgo&apos;s content except as permitted by
                these Terms or with express written consent.
              </p>
              <p className='text-gray-700'>
                The Nexgo name, logos, and associated visuals are registered or
                common law trademarks.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Third-Party Services and External Links
              </h2>
              <p className='text-gray-700 mb-4'>
                Nexgo may integrate with or link to third-party services such as
                booking engines, payment providers, or map APIs (e.g., Google
                Maps). Nexgo is not liable for:
              </p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2'>
                <li>Accuracy or availability of third-party services</li>
                <li>Privacy or data practices of external platforms</li>
                <li>Disputes between users and external service providers</li>
              </ul>
            </section>

            {/* Termination */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Termination
              </h2>
              <p className='text-gray-700'>
                We may suspend or terminate your access for any breach of these
                Terms or misuse of the Service. You may terminate your account
                at any time by emailing legal@nexgo.ai.
              </p>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Indemnification
              </h2>
              <p className='text-gray-700'>
                You agree to indemnify and hold harmless Nexgo AI Labs Inc. and
                its affiliates from claims, damages, liabilities, and expenses
                arising from your use of the Service, interactions with
                providers, or violation of these Terms.
              </p>
            </section>

            {/* Disclaimers */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Disclaimers
              </h2>
              <div className='bg-red-50 border-l-4 border-red-400 p-4 mb-4'>
                <p className='text-red-800 font-semibold'>
                  THE SERVICE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTIES
                  OF ANY KIND. NEXGO DISCLAIMS ALL IMPLIED WARRANTIES INCLUDING
                  MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                  NON-INFRINGEMENT.
                </p>
              </div>
              <p className='text-gray-700'>
                WE DO NOT GUARANTEE UNINTERRUPTED ACCESS, DATA ACCURACY, OR
                AVAILABILITY OF EXPERIENCES OR PRODUCTS LISTED BY THIRD PARTIES.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Limitation of Liability
              </h2>
              <div className='bg-red-50 border-l-4 border-red-400 p-4 mb-4'>
                <p className='text-red-800 font-semibold'>
                  TO THE EXTENT PERMITTED BY LAW, NEXGO IS NOT LIABLE FOR
                  INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR SPECIAL DAMAGES
                  ARISING FROM YOUR USE OF THE SERVICE.
                </p>
              </div>
              <p className='text-gray-700'>
                TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO NEXGO IN
                THE SIX MONTHS PRIOR TO THE CLAIM OR $100, WHICHEVER IS GREATER.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Governing Law and Dispute Resolution
              </h2>
              <p className='text-gray-700'>
                These Terms are governed by the laws of the State of Delaware.
                All disputes shall be resolved through binding arbitration under
                the rules of the American Arbitration Association (AAA) in Santa
                Clara County, California. You waive the right to participate in
                any class action.
              </p>
            </section>

            {/* Miscellaneous */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Miscellaneous
              </h2>
              <ul className='list-disc pl-6 text-gray-700 space-y-2'>
                <li>
                  These Terms constitute the full agreement between you and
                  Nexgo.
                </li>
                <li>
                  Nexgo is not liable for delays due to events beyond its
                  control (force majeure).
                </li>
                <li>
                  You may not assign your rights under these Terms without prior
                  consent.
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                Contact
              </h2>
              <p className='text-gray-700 mb-2'>
                For legal inquiries or support:
              </p>
              <div className='bg-gray-50 p-4 rounded-lg'>
                <p className='text-gray-700 font-semibold'>
                  Nexgo AI Labs Inc.
                </p>
                <p className='text-gray-700'>
                  16192 Coastal Highway, Lewes, DE 19958
                </p>
                <p className='text-gray-700'>
                  Email:{' '}
                  <a
                    href='mailto:legal@nexgo.ai'
                    className='text-blue-600 hover:text-blue-800'
                  >
                    legal@nexgo.ai
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}
